import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type {
  TabContent,
  TabContentAction,
} from '../CustomDash/components/PageConfigProvider/page-reducer';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import { pickLiveChartPreviewMessageIds } from './global-ai-assistant-chart-preview-policy';
import {
  createIdleChartPreviewRuntime,
  type GlobalAiChartPreviewRuntimeSnapshot,
  type GlobalAiChartPreviewTimelineEvent,
  type GlobalAiPipelineTimelineEvent,
} from './global-ai-assistant-chart-preview-runtime';
import type { GlobalAiConversationSummary } from './global-ai-assistant-conversation';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import type { ReasoningTrace } from './reasoning-trace';
import { useGlobalAiConversationSummarizer } from './use-global-ai-conversation-summarizer';

export type GlobalAiApplyAiSearchFiltersOptions = {
  primarySelectionMergeMode?: 'merge' | 'replace';
  skipTalentDiscoveryAdaptFallback?: boolean;
};

export type GlobalAiDashboardBinding = {
  entityType: GlobalAssistantEntityType;
  dispatch: (action: TabContentAction) => void;
  getTabContent: () => TabContent;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: GlobalAiApplyAiSearchFiltersOptions
  ) => Promise<unknown>;
  getTaxonomyPayload: () => { rolesTax?: string; skillsTax?: number };
};

export type GlobalAiPendingWork =
  | {
      kind: 'customPageStream';
      prompt: string;
      model?: string;
      entityType: GlobalAssistantEntityType;
      /** Default create when omitted (backward compatible). */
      mode?: 'create' | 'update';
    }
  | {
      kind: 'singleChartApply';
      config: LLMTabContentForDispatch;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      entityType: GlobalAssistantEntityType;
    }
  | {
      kind: 'routeFilterApply';
      prompt: string;
      targetPath: string;
      entityType: GlobalAssistantEntityType;
      requestId: string;
    };

export type GlobalAiCustomDashAssistContext = {
  isInCustomDashEditMode: boolean;
  hasCurrentPageItems: boolean;
  hasUnsavedChanges: boolean;
  discardChanges?: () => void;
};

type GlobalAiAssistantContextValue = {
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** Persists draft text while the drawer is unmounted; not in context value deps. */
  draftPromptRef: React.MutableRefObject<string>;
  hasRequestedWarmUp: boolean;
  markWarmUpRequested: () => void;
  /** Latest dashboard tab binding; committed in `GlobalAIAssistantBridge` via `useLayoutEffect` (no setState). */
  bindingRef: React.MutableRefObject<GlobalAiDashboardBinding | null>;
  /** Custom Dash edit surface hints for global assistant requests (updated by bridge). */
  customDashAssistContextRef: React.MutableRefObject<GlobalAiCustomDashAssistContext>;
  pending: GlobalAiPendingWork | null;
  setPending: Dispatch<SetStateAction<GlobalAiPendingWork | null>>;
  lastUserPrompt: string | null;
  setLastUserPrompt: (value: string | null) => void;
  generationStatus: string | null;
  setGenerationStatus: (value: string | null) => void;
  streamAbortRef: React.MutableRefObject<AbortController | null>;
  /**
   * Abort controller for the active tool-loop turn. Separate from
   * `streamAbortRef` (which the bridge reuses for page-stream SSE and would
   * abort mid-turn). Set by the turn runner; aborted on drawer close so an
   * in-flight turn's SSE + poll waits actually stop (FE-2117).
   */
  turnAbortRef: React.MutableRefObject<AbortController | null>;
  transcript: GlobalAiTranscriptMessage[];
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  updateAssistantMessageText: (id: string, text: string) => void;
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  /**
   * Mark a `nav_confirm` chip resolved so it can't re-fire after the user
   * actions it (clicks "Go to …").
   */
  markNavConfirmResolved: (id: string) => void;
  /**
   * Mark a `create_page_confirm` chip resolved so it can't re-fire after the
   * user clicks "Build the dashboard".
   */
  markCreatePageConfirmResolved: (id: string) => void;
  clearTranscript: () => void;
  /** DOM mount nodes for each `single_chart` transcript message id (for portaled charts). */
  chartPreviewMounts: Readonly<Record<string, HTMLElement>>;
  registerChartPreviewMount: (
    messageId: string,
    el: HTMLElement | null
  ) => void;
  /** Transcript scroll visibility for chart slots (IntersectionObserver). */
  setChartPreviewSlotViewportVisible: (
    messageId: string,
    visible: boolean
  ) => void;
  /** Message ids that should mount ChartRuntimeShell + chart portal (capped). */
  liveChartPreviewMessageIds: readonly string[];
  /** Per chart-message preview lifecycle + diagnostics (not persisted). */
  chartPreviewRuntimeByMessageId: Readonly<
    Record<string, GlobalAiChartPreviewRuntimeSnapshot>
  >;
  /** Per request submit -> backend/orchestration timeline (not persisted). */
  pipelineTimelineByRequestId: Readonly<
    Record<string, GlobalAiPipelineTimelineEvent[]>
  >;
  mergeChartPreviewRuntime: (
    messageId: string,
    patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
  ) => void;
  addChartPreviewTimelineEvent: (messageId: string, event: string) => void;
  addPipelineTimelineEvent: (requestId: string, event: string) => void;
  sessionEntityScope: GlobalAssistantEntityType | null;
  setSessionEntityScope: (value: GlobalAssistantEntityType | null) => void;
  /**
   * Compact summary of older transcript turns. Set asynchronously by
   * `useGlobalAiConversationSummarizer`; `null` until the transcript
   * grows past the recency window.
   */
  conversationSummary: GlobalAiConversationSummary | null;
  setConversationSummary: Dispatch<
    SetStateAction<GlobalAiConversationSummary | null>
  >;
  conversationTrackingIdRef: React.MutableRefObject<string>;
};

const GlobalAiAssistantContext = createContext<
  GlobalAiAssistantContextValue | undefined
>(undefined);

export function GlobalAIAssistantProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const draftPromptRef = useRef('');
  const [hasRequestedWarmUp, setHasRequestedWarmUp] = useState(false);
  const bindingRef = useRef<GlobalAiDashboardBinding | null>(null);
  const customDashAssistContextRef = useRef<GlobalAiCustomDashAssistContext>({
    isInCustomDashEditMode: false,
    hasCurrentPageItems: false,
    hasUnsavedChanges: false,
    discardChanges: undefined,
  });
  const [pending, setPending] = useState<GlobalAiPendingWork | null>(null);
  const [lastUserPrompt, setLastUserPrompt] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const turnAbortRef = useRef<AbortController | null>(null);
  const conversationTrackingIdRef = useRef(crypto.randomUUID());
  const [transcript, setTranscript] = useState<GlobalAiTranscriptMessage[]>([]);
  const [chartPreviewMounts, setChartPreviewMounts] = useState<
    Record<string, HTMLElement>
  >({});
  const [chartPreviewViewportVisible, setChartPreviewViewportVisible] =
    useState<Record<string, boolean>>({});
  const [sessionEntityScope, setSessionEntityScope] =
    useState<GlobalAssistantEntityType | null>(null);
  const [chartPreviewRuntimeByMessageId, setChartPreviewRuntimeByMessageId] =
    useState<Record<string, GlobalAiChartPreviewRuntimeSnapshot>>({});
  const [pipelineTimelineByRequestId, setPipelineTimelineByRequestId] =
    useState<Record<string, GlobalAiPipelineTimelineEvent[]>>({});
  const [conversationSummary, setConversationSummary] =
    useState<GlobalAiConversationSummary | null>(null);

  const mergeChartPreviewRuntime = useCallback(
    (
      messageId: string,
      patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
    ) => {
      setChartPreviewRuntimeByMessageId((prev) => {
        const current = prev[messageId] ?? createIdleChartPreviewRuntime();
        const next: GlobalAiChartPreviewRuntimeSnapshot = {
          ...current,
          ...patch,
        };
        const changed = Object.keys(patch).some((key) => {
          const typedKey = key as keyof GlobalAiChartPreviewRuntimeSnapshot;
          return current[typedKey] !== next[typedKey];
        });
        if (!changed) {
          return prev;
        }
        return { ...prev, [messageId]: next };
      });
    },
    []
  );

  const addChartPreviewTimelineEvent = useCallback(
    (messageId: string, event: string) => {
      const nextEvent: GlobalAiChartPreviewTimelineEvent = {
        atMs: Date.now(),
        event,
      };
      setChartPreviewRuntimeByMessageId((prev) => {
        const current = prev[messageId] ?? createIdleChartPreviewRuntime();
        const timeline = [...(current.timeline ?? []), nextEvent];
        const trimmed =
          timeline.length > 30
            ? timeline.slice(timeline.length - 30)
            : timeline;
        return {
          ...prev,
          [messageId]: {
            ...current,
            timeline: trimmed,
          },
        };
      });
    },
    []
  );

  const addPipelineTimelineEvent = useCallback(
    (requestId: string, event: string) => {
      const nextEvent: GlobalAiPipelineTimelineEvent = {
        atMs: Date.now(),
        event,
      };
      setPipelineTimelineByRequestId((prev) => {
        const timeline = [...(prev[requestId] ?? []), nextEvent];
        const trimmed =
          timeline.length > 30
            ? timeline.slice(timeline.length - 30)
            : timeline;
        return {
          ...prev,
          [requestId]: trimmed,
        };
      });
    },
    []
  );

  const appendTranscript = useCallback((message: GlobalAiTranscriptMessage) => {
    setTranscript((prev) => {
      // Defense-in-depth for the FE-2117 single-narration invariant. That
      // invariant currently rests entirely on callers keying every message
      // uniquely: the streamed-text path (the turn runner's `onText`) appends a
      // stable per-step segment id exactly once — gated by its
      // `appendedSegmentIds` Set — then switches to `updateAssistantMessageText`;
      // every other caller mints a fresh `crypto.randomUUID()`. So a
      // duplicate-id append never happens under correct operation. This guard
      // makes appending an already-present id a NO-OP so that a future caller or
      // a stray double-invoke can never silently re-introduce the
      // narration-duplication artifact. O(n) over the (small, single-conversation)
      // transcript.
      if (prev.some((existing) => existing.id === message.id)) {
        return prev;
      }
      return [...prev, message];
    });
  }, []);

  const updateAssistantMessageText = useCallback((id: string, text: string) => {
    setTranscript((previous) =>
      previous.map((message) =>
        message.id === id && 'text' in message ? { ...message, text } : message
      )
    );
  }, []);

  const updateReasoningTrace = useCallback(
    (id: string, trace: ReasoningTrace) => {
      setTranscript((previous) =>
        previous.map((message) =>
          message.id === id &&
          message.role === 'assistant' &&
          message.kind === 'reasoning_trace'
            ? { ...message, trace }
            : message
        )
      );
    },
    []
  );

  const markNavConfirmResolved = useCallback((id: string) => {
    setTranscript((previous) =>
      previous.map((message) =>
        message.id === id &&
        message.role === 'assistant' &&
        message.kind === 'nav_confirm'
          ? { ...message, resolved: true }
          : message
      )
    );
  }, []);

  const markCreatePageConfirmResolved = useCallback((id: string) => {
    setTranscript((previous) =>
      previous.map((message) =>
        message.id === id &&
        message.role === 'assistant' &&
        message.kind === 'create_page_confirm'
          ? { ...message, resolved: true }
          : message
      )
    );
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript([]);
    draftPromptRef.current = '';
    setChartPreviewMounts({});
    setChartPreviewViewportVisible({});
    setChartPreviewRuntimeByMessageId({});
    setPipelineTimelineByRequestId({});
    setSessionEntityScope(null);
    setConversationSummary(null);
  }, []);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    setIsDrawerOpen(false);
  }, []);

  const markWarmUpRequested = useCallback(() => {
    setHasRequestedWarmUp(true);
  }, []);

  const registerChartPreviewMount = useCallback(
    (messageId: string, el: HTMLElement | null) => {
      if (el !== null) {
        mergeChartPreviewRuntime(messageId, { phase: 'mounting' });
        addChartPreviewTimelineEvent(messageId, 'mount attached');
      }
      setChartPreviewMounts((prev) => {
        if (el === null) {
          if (!(messageId in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        if (prev[messageId] === el) {
          return prev;
        }
        return { ...prev, [messageId]: el };
      });
    },
    [addChartPreviewTimelineEvent, mergeChartPreviewRuntime]
  );

  const setChartPreviewSlotViewportVisible = useCallback(
    (messageId: string, visible: boolean) => {
      setChartPreviewViewportVisible((prev) => {
        if (prev[messageId] === visible) {
          return prev;
        }
        return { ...prev, [messageId]: visible };
      });
      if (visible) {
        addChartPreviewTimelineEvent(messageId, 'slot became visible');
      }
    },
    [addChartPreviewTimelineEvent]
  );

  const singleChartMessageIdsOrdered = useMemo(() => {
    return (
      transcript
        .filter(
          (
            m
          ): m is Extract<
            GlobalAiTranscriptMessage,
            { kind: 'single_chart' }
          > => m.role === 'assistant' && m.kind === 'single_chart'
        )
        // answerAsText single_chart messages are offer-wiring metadata only:
        // their digest comes from the headless service, so they must never
        // occupy live chart-preview slots.
        .filter((m) => m.answerAsText !== true)
        .map((m) => m.id)
    );
  }, [transcript]);

  const viewportVisibleIds = useMemo(() => {
    return new Set(
      Object.entries(chartPreviewViewportVisible)
        .filter(([, v]) => v)
        .map(([k]) => k)
    );
  }, [chartPreviewViewportVisible]);

  const liveChartPreviewMessageIds = useMemo(
    () =>
      pickLiveChartPreviewMessageIds(
        singleChartMessageIdsOrdered,
        viewportVisibleIds
      ),
    [singleChartMessageIdsOrdered, viewportVisibleIds]
  );

  useGlobalAiConversationSummarizer({
    transcript,
    summary: conversationSummary,
    setSummary: setConversationSummary,
  });

  const value = useMemo(
    () => ({
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      draftPromptRef,
      hasRequestedWarmUp,
      markWarmUpRequested,
      bindingRef,
      customDashAssistContextRef,
      pending,
      setPending,
      lastUserPrompt,
      setLastUserPrompt,
      generationStatus,
      setGenerationStatus,
      streamAbortRef,
      turnAbortRef,
      transcript,
      appendTranscript,
      updateAssistantMessageText,
      updateReasoningTrace,
      markNavConfirmResolved,
      markCreatePageConfirmResolved,
      clearTranscript,
      chartPreviewMounts,
      registerChartPreviewMount,
      setChartPreviewSlotViewportVisible,
      liveChartPreviewMessageIds,
      chartPreviewRuntimeByMessageId,
      pipelineTimelineByRequestId,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      addPipelineTimelineEvent,
      sessionEntityScope,
      setSessionEntityScope,
      conversationSummary,
      setConversationSummary,
      conversationTrackingIdRef,
    }),
    [
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      hasRequestedWarmUp,
      markWarmUpRequested,
      pending,
      lastUserPrompt,
      generationStatus,
      transcript,
      appendTranscript,
      updateAssistantMessageText,
      updateReasoningTrace,
      markNavConfirmResolved,
      markCreatePageConfirmResolved,
      clearTranscript,
      chartPreviewMounts,
      registerChartPreviewMount,
      setChartPreviewSlotViewportVisible,
      liveChartPreviewMessageIds,
      chartPreviewRuntimeByMessageId,
      pipelineTimelineByRequestId,
      mergeChartPreviewRuntime,
      addChartPreviewTimelineEvent,
      addPipelineTimelineEvent,
      sessionEntityScope,
      conversationSummary,
    ]
  );

  return (
    <GlobalAiAssistantContext.Provider value={value}>
      {children}
    </GlobalAiAssistantContext.Provider>
  );
}

export function useGlobalAIAssistant(): GlobalAiAssistantContextValue {
  const ctx = useContext(GlobalAiAssistantContext);
  if (!ctx) {
    throw new Error(
      'useGlobalAIAssistant must be used within GlobalAIAssistantProvider'
    );
  }
  return ctx;
}

export function useGlobalAIAssistantOptional():
  | GlobalAiAssistantContextValue
  | undefined {
  return useContext(GlobalAiAssistantContext);
}
